import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { getAuthInstance, initializeFirebase } from '../firebase';
import LoadingScreen from '../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

type LoginStatus = 'validating' | 'signing_in' | 'success' | 'error';

const TokenLoginPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<LoginStatus>('validating');
  const [error, setError] = useState<string | null>(null);

  // Get redirect destination from query params, default to profile
  const redirectTo = searchParams.get('redirect') || '/app/profile';

  useEffect(() => {
    const performTokenLogin = async () => {
      if (!token) {
        setStatus('error');
        setError('No login token provided');
        return;
      }

      try {
        // Ensure Firebase is initialized
        await initializeFirebase();

        // Step 1: Validate token with server
        setStatus('validating');
        const response = await fetch(`${API_BASE_URL}/api/auth/validate-login-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Invalid or expired token');
        }

        const { customToken } = await response.json();

        // Step 2: Sign in with Firebase custom token
        setStatus('signing_in');
        const auth = getAuthInstance();
        await signInWithCustomToken(auth, customToken);

        // Step 3: Redirect to destination
        setStatus('success');

        // Small delay to show success state
        setTimeout(() => {
          navigate(redirectTo, { replace: true });
        }, 500);
      } catch (err) {
        console.error('Token login error:', err);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Failed to authenticate');
      }
    };

    performTokenLogin();
  }, [token, navigate, redirectTo]);

  if (status === 'error') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>!</div>
          <h2 style={styles.title}>Login Failed</h2>
          <p style={styles.errorMessage}>{error}</p>
          <p style={styles.helpText}>
            This link may have expired or already been used.
            Please try again from the mobile app.
          </p>
          <button
            onClick={() => navigate('/login')}
            style={styles.button}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const messages: Record<LoginStatus, { message: string; submessage: string }> = {
    validating: {
      message: 'Validating...',
      submessage: 'Checking your login credentials',
    },
    signing_in: {
      message: 'Signing in...',
      submessage: 'Almost there!',
    },
    success: {
      message: 'Success!',
      submessage: 'Redirecting you now...',
    },
    error: {
      message: 'Error',
      submessage: 'Something went wrong',
    },
  };

  const currentMessage = messages[status];

  return (
    <LoadingScreen
      message={currentMessage.message}
      submessage={currentMessage.submessage}
    />
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  errorIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    fontSize: '32px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '12px',
  },
  errorMessage: {
    fontSize: '16px',
    color: '#dc2626',
    marginBottom: '8px',
  },
  helpText: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '24px',
  },
  button: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
  },
};

export default TokenLoginPage;
